import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sltAPI } from '../../services/api';
import SLTLayout from './SLTLayout';
import { BRAND, cardStyle, tdStyle, thStyle, searchInputStyle, formatDate, StatPill, TagBadge, ClickableRow, EmptyState, StudentActivityFeed } from './sltUi';

const FETCH_LIMIT = 500;

const getYearSortValue = (yearName = '') => {
  const match = String(yearName).match(/\d+/);
  return match ? parseInt(match[0], 10) : Number.MAX_SAFE_INTEGER;
};

// A student's primary year group: the lowest year among their classes.
const primaryYearGroup = (student) => {
  const years = (student.classes || []).map((c) => c.yearGroup).filter(Boolean);
  if (!years.length) return 'Year not set';
  return years.sort((a, b) => getYearSortValue(a) - getYearSortValue(b) || a.localeCompare(b))[0];
};

const groupStudentsByYear = (students = []) => {
  const yearMap = new Map();
  students.forEach((s) => {
    const year = primaryYearGroup(s);
    if (!yearMap.has(year)) yearMap.set(year, []);
    yearMap.get(year).push(s);
  });
  return Array.from(yearMap.entries())
    .sort((a, b) => getYearSortValue(a[0]) - getYearSortValue(b[0]) || a[0].localeCompare(b[0]))
    .map(([year, list]) => ({ year, students: list }));
};

const SLTStudents = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [studentActivity, setStudentActivity] = useState(null);

  useEffect(() => {
    let isMounted = true;
    sltAPI.getOverview()
      .then((res) => { if (isMounted) setStudentActivity(res.data?.recentStudentActivity || []); })
      .catch(() => { if (isMounted) setStudentActivity([]); });
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const timer = setTimeout(() => {
      sltAPI.getStudents({ page: 1, limit: FETCH_LIMIT, search: search.trim() || undefined })
        .then((res) => { if (isMounted) setData(res.data); })
        .catch(() => { if (isMounted) setError('Unable to load students.'); });
    }, search ? 350 : 0);
    return () => { isMounted = false; clearTimeout(timer); };
  }, [search]);

  const students = useMemo(() => data?.students || [], [data]);
  const yearGroups = useMemo(() => groupStudentsByYear(students), [students]);

  return (
    <SLTLayout
      user={user}
      onLogout={onLogout}
      title="Students"
      subtitle="Every student in your school with their needs, provision and performance, grouped by year. Click a student for the full scaffolding and intervention audit."
      actions={(
        <input
          type="search"
          aria-label="Search students"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search students by name…"
          style={searchInputStyle}
        />
      )}
    >
      <style>{`
        .slt-year-section summary { cursor: pointer; list-style: none; }
        .slt-year-section summary::-webkit-details-marker { display: none; }
        .slt-year-section summary:hover { color: ${BRAND.coral}; }
        .slt-year-section[open] > summary .slt-caret { transform: rotate(90deg); }
        .slt-caret { display: inline-block; transition: transform 0.15s ease; }
      `}</style>

      {error && <p style={{ color: '#B3261E', fontWeight: 700 }}>{error}</p>}
      {!data && !error && <p style={{ color: BRAND.muted }}>Loading students…</p>}

      {data && students.length === 0 && (
        <section style={cardStyle}>
          <EmptyState title="No students found" hint={search ? 'Try a different name.' : 'Students will appear once teachers add them to classes.'} />
        </section>
      )}

      {data && students.length > 0 && (
        <>
          <p style={{ color: BRAND.muted, fontSize: '0.9rem', margin: '0 0 16px' }}>
            {data.totalStudents} student{data.totalStudents === 1 ? '' : 's'}{search ? ' matching search' : ''} across {yearGroups.length} year group{yearGroups.length === 1 ? '' : 's'}.
            {data.totalStudents > FETCH_LIMIT && ` Showing the first ${FETCH_LIMIT} — refine your search to narrow down.`}
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {yearGroups.map((group) => (
              <details key={group.year} className="slt-year-section" open={search ? true : undefined} style={{ ...cardStyle, padding: 0 }}>
                <summary style={{ alignItems: 'center', display: 'flex', gap: '10px', fontFamily: 'Nunito, sans-serif', fontSize: '1.1rem', fontWeight: 800, padding: '16px 20px' }}>
                  <span className="slt-caret" aria-hidden="true">▸</span>
                  {group.year}
                  <span style={{ color: BRAND.muted, fontSize: '0.9rem', fontWeight: 700 }}>
                    ({group.students.length} student{group.students.length === 1 ? '' : 's'})
                  </span>
                </summary>

                <div style={{ borderTop: '1.5px solid rgba(45,64,66,0.15)', overflowX: 'auto' }}>
                  <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                    <thead>
                      <tr>
                        <th style={thStyle}>Student</th>
                        <th style={thStyle}>Teachers</th>
                        <th style={thStyle}>Classes</th>
                        <th style={thStyle}>Needs / scaffolding</th>
                        <th style={thStyle}>Assigned</th>
                        <th style={thStyle}>Completion</th>
                        <th style={thStyle}>Accuracy</th>
                        <th style={thStyle}>Last active</th>
                        <th style={thStyle} aria-hidden="true" />
                      </tr>
                    </thead>
                    <tbody>
                      {group.students.map((s) => (
                        <ClickableRow key={s.studentId} onActivate={() => navigate(`/slt/students/${s.studentId}`)}>
                          <td style={tdStyle}><span style={{ fontWeight: 800 }}>{s.name}</span></td>
                          <td style={{ ...tdStyle, maxWidth: '180px' }}>{(s.teacherNames || []).join(', ') || '—'}</td>
                          <td style={{ ...tdStyle, maxWidth: '220px' }} title={(s.classes || []).map((c) => `${c.name}${c.yearGroup ? ` · ${c.yearGroup}` : ''}`).join(', ')}>
                            {(s.classes || []).slice(0, 2).map((c) => (
                              <TagBadge key={c.name}>{c.name}{c.yearGroup ? ` · ${c.yearGroup}` : ''}</TagBadge>
                            ))}
                            {(s.classes || []).length > 2 && <TagBadge tone="coral">+{s.classes.length - 2} more</TagBadge>}
                            {!(s.classes || []).length && <span style={{ color: BRAND.muted }}>—</span>}
                          </td>
                          <td style={{ ...tdStyle, maxWidth: '250px' }}>
                            {s.senConditions.map((c) => <TagBadge key={c} tone="coral">{c}</TagBadge>)}
                            {s.eal && <TagBadge>EAL{s.primaryLanguage ? ` · ${s.primaryLanguage}` : ''}</TagBadge>}
                            {s.audioNarrationEnabled && <TagBadge>Audio narration</TagBadge>}
                            {!s.senConditions.length && !s.eal && !s.audioNarrationEnabled && <span style={{ color: BRAND.muted }}>—</span>}
                          </td>
                          <td style={tdStyle}>{s.tutorialsAssigned}</td>
                          <td style={tdStyle}><StatPill value={s.completionRate} /></td>
                          <td style={tdStyle}><StatPill value={s.averageAccuracy} /></td>
                          <td style={tdStyle}>{formatDate(s.lastActivity)}</td>
                          <td style={{ ...tdStyle, color: BRAND.muted, textAlign: 'right' }} aria-hidden="true">›</td>
                        </ClickableRow>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            ))}
          </div>

          <section style={{ ...cardStyle, marginTop: '26px' }}>
            <h2 style={{ fontSize: '1.25rem', margin: '0 0 4px' }}>Recent student activity</h2>
            <p style={{ color: BRAND.muted, fontSize: '0.88rem', margin: '0 0 12px' }}>The latest digital tutorials taken by students. Click a row to open the student's profile.</p>
            {studentActivity === null
              ? <p style={{ color: BRAND.muted, margin: 0 }}>Loading activity…</p>
              : <StudentActivityFeed items={studentActivity} />}
          </section>
        </>
      )}
    </SLTLayout>
  );
};

export default SLTStudents;
